import { useState } from "react";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Toast from "../components/Toast";
import { CLIENT_COLORS, gapPct, totalPct } from "../constants";
import { apiFetch } from "../utils/api";

// Quick-sort options for the page-level employee list. Kept in sync with the
// Employee Database page so users see the same labels everywhere.
const SORT_OPTIONS = [
  { value: "code_asc", label: "Employee Id  A → Z", by: "code", dir: "asc" },
  { value: "code_desc", label: "Employee Id  Z → A", by: "code", dir: "desc" },
  { value: "name_asc", label: "Name  A → Z", by: "name", dir: "asc" },
  { value: "name_desc", label: "Name  Z → A", by: "name", dir: "desc" },
  { value: "joined_desc", label: "DOJ (Newest First)", by: "joined", dir: "desc" },
  { value: "joined_asc", label: "DOJ (Oldest First)", by: "joined", dir: "asc" },
];

const AllocationPage = ({ clients, employees, setEmployees, topBarProps, onAddNewEmployee, currentRole }) => {
  const [filter, setFilter] = useState("all");
  const [empSel, setEmpSel] = useState(null);
  const [drafts, setDrafts] = useState([]);
  // Default sort: Employee Id A → Z, same as the Employee Database page.
  const [sortBy, setSortBy] = useState("code");
  const [sortDir, setSortDir] = useState("asc");
  const dropdownVal = SORT_OPTIONS.find(o => o.by === sortBy && o.dir === sortDir)?.value || "code_asc";
  const applyDropdownSort = val => {
    const opt = SORT_OPTIONS.find(o => o.value === val);
    if (opt) { setSortBy(opt.by); setSortDir(opt.dir); }
  };
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };
  // Text search across name / id / role — mirrors the Review Management toolbar.
  const [search, setSearch] = useState("");
  const searchNorm = search.trim().toLowerCase();

  const counts = {
    all: employees.length,
    full: employees.filter(e => totalPct(e) === 100).length,
    under: employees.filter(e => { const t = totalPct(e); return t > 0 && t < 100; }).length,
    unallocated: employees.filter(e => totalPct(e) === 0).length,
    multi: employees.filter(e => (e.allocations || []).length > 1).length,
    leakage: employees.filter(e => gapPct(e) > 0).length,
  };
  const filtered = employees.filter(e => {
    const t = totalPct(e);
    if (filter === "full" && t !== 100) return false;
    if (filter === "under" && !(t > 0 && t < 100)) return false;
    if (filter === "unallocated" && t !== 0) return false;
    if (filter === "multi" && !((e.allocations || []).length > 1)) return false;
    if (filter === "leakage" && !(gapPct(e) > 0)) return false;
    // Text search — match on name / id / role
    if (searchNorm) {
      const haystack = [e.name, e.code, e.role].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(searchNorm)) return false;
    }
    return true;
  }).sort((a, b) => {
    let va, vb;
    if (sortBy === "name") { va = a.name || ""; vb = b.name || ""; }
    else if (sortBy === "joined") { va = a.joinDate || ""; vb = b.joinDate || ""; }
    else { va = a.code || ""; vb = b.code || ""; } // default: code
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const openEdit = emp => { setEmpSel(emp.id); setDrafts((emp.allocations || []).map(a => ({ ...a }))); };
  const closeEdit = () => { setEmpSel(null); setDrafts([]); };
  const draftTotal = drafts.reduce((s, a) => s + Number(a.pct), 0);
  const draftGap = 100 - draftTotal;

  const addDraft = () => {
    const used = drafts.map(a => a.clientId);
    const avail = clients.filter(c => !used.includes(c.id) && c.status === "active");
    if (!avail.length) return;
    const c = avail[0];
    setDrafts(p => [...p, { clientId: c.id, deptId: c.departments[0] ? c.departments[0].id : "", clientName: c.name, pct: Math.min(draftGap, 100), color: c.color || c.color_hex || CLIENT_COLORS[c.id] || "#64748B", stakeholders: [] }]);
  };
  const remDraft = idx => setDrafts(p => p.filter((_, i) => i !== idx));
  const updPct = (idx, v) => setDrafts(p => p.map((a, i) => i === idx ? { ...a, pct: Math.max(0, Math.min(100, Number(v))) } : a));
  const updClient = (idx, cid) => { const c = clients.find(x => x.id === cid); setDrafts(p => p.map((a, i) => i === idx ? { ...a, clientId: cid, clientName: c ? c.name : "", deptId: (c && c.departments[0]) ? c.departments[0].id : "", color: (c && (c.color || c.color_hex)) || CLIENT_COLORS[cid] || "#64748B", stakeholders: [] } : a)); };
  const updDept = (idx, did) => setDrafts(p => p.map((a, i) => i === idx ? { ...a, deptId: did } : a));

  const updStakeholderPct = (draftIdx, shId, rawVal, maxPct) => {
    setDrafts(p => p.map((a, i) => {
      if (i !== draftIdx) return a;
      const existing = a.stakeholders || [];
      const others = existing.filter(s => s.stakeholderId !== shId);
      const v = Number(rawVal);
      if (!rawVal || v === 0) return { ...a, stakeholders: others };
      return { ...a, stakeholders: [...others, { stakeholderId: shId, pct: Math.min(v, maxPct) }] };
    }));
  };
  const resetStakeholderSplit = (draftIdx) => {
    setDrafts(p => p.map((a, i) => i === draftIdx ? { ...a, stakeholders: [] } : a));
  };
  const saveDrafts = async eid => {
    if (drafts.length === 0) { showToast("Please add at least one client before saving", "error"); return; }  // ← ADD THIS
    if (draftTotal > 100) { showToast("Total exceeds 100% — please fix", "error"); return; }
    // If a client has active stakeholders, an explicit stakeholder split that
    // totals the allocation pct is REQUIRED — saving with all stakeholder
    // inputs left at 0 / empty silently lost the assignment intent.
    const badSplit = drafts.find(a => {
      const cl = clients.find(c => c.id === a.clientId);
      const activeSH = cl ? (cl.stakeholders || []).filter(s => s.active) : [];
      if (activeSH.length === 0) return false; // no stakeholders configured — nothing to split
      const shTotal = (a.stakeholders || []).reduce((s, x) => s + Number(x.pct), 0);
      return shTotal !== Number(a.pct);
    });
    if (badSplit) {
      const cl = clients.find(c => c.id === badSplit.clientId);
      const who = cl ? (cl.name || "").split(" ")[0] : "client";
      const shTotal = (badSplit.stakeholders || []).reduce((s, x) => s + Number(x.pct), 0);
      const msg = shTotal === 0
        ? `Please assign a stakeholder split totaling ${badSplit.pct}% for ${who}`
        : `Stakeholder split for ${who} must total ${badSplit.pct}% (currently ${shTotal}%)`;
      showToast(msg, "error"); return;
    }
    const prevEmployees = [...employees];
    setEmployees(p => p.map(e => e.id === eid ? { ...e, allocations: drafts } : e));
    closeEdit();
    try {
      const res = await apiFetch("/api/allocations", { method: "POST", body: JSON.stringify({ employee_id: eid, allocations: drafts }) });
      const d = await res.json();
      if (!res.ok || !d.success) {
        setEmployees(prevEmployees);
        showToast("Error: " + (d.message || "Save failed"), "error");
        return;
      }
      showToast("Allocation saved", "success");
    } catch (e) {
      console.error("[saveDrafts] exception:", e);
      setEmployees(prevEmployees);
      showToast("Network error — allocation not saved", "error");
    }
  };

  const leakEmps = employees.filter(e => gapPct(e) > 0);

  return (
    <div className="fade-in">
      <TopBar title="Allocation & Billing Leakage" subtitle="Filter, diagnose and fix employee allocation gaps inline" {...topBarProps} />

      {currentRole !== "Sub-Admin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button className="btn-primary" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 7 }}
            onClick={onAddNewEmployee}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> New Employee
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
        {[
          { key: "full", label: "Fully Allocated", v: counts.full, sub: "100% deployed", c: "#10B981", bg: "#F0FDF4", activeBg: "#DCFCE7" },
          { key: "under", label: "Under-Allocated", v: counts.under, sub: "partial gap", c: "#F59E0B", bg: "#FFFBEB", activeBg: "#FEF3C7" },
          { key: "unallocated", label: "Unallocated", v: counts.unallocated, sub: "not deployed", c: "#EF4444", bg: "#FEF2F2", activeBg: "#FEE2E2" },
          { key: "multi", label: "Multi-Client Split", v: counts.multi, sub: "2+ clients", c: "#6366F1", bg: "#EEF2FF", activeBg: "#E0E7FF" },
        ].map(s => {
          const isActive = filter === s.key;
          return (
            <div key={s.key} className="card fade-up"
              onClick={() => setFilter(isActive ? "all" : s.key)}
              style={{
                padding: "16px 18px", cursor: "pointer", transition: "all .18s",
                borderTop: `3px solid ${s.c}`,
                background: isActive ? s.activeBg : s.bg,
                outline: isActive ? `2px solid ${s.c}` : "2px solid transparent",
                outlineOffset: -2,
                boxShadow: isActive ? `0 4px 14px ${s.c}30` : undefined,
                userSelect: "none",
              }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 26, color: s.c, letterSpacing: -1 }}>{s.v}</div>
                {isActive && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: s.c, background: s.c + "18", border: `1px solid ${s.c}44`, borderRadius: 100, padding: "2px 7px", marginTop: 2, whiteSpace: "nowrap" }}>
                    Active ✕
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1E293B", marginTop: 4 }}>{s.label}</div>
              <div style={{ fontSize: 11, color: isActive ? s.c : "#94A3B8", marginTop: 2, fontWeight: isActive ? 600 : 400 }}>
                {isActive ? `Showing ${s.v} employee${s.v !== 1 ? "s" : ""}` : s.sub}
              </div>
            </div>
          );
        })}
      </div>

      {leakEmps.length > 0 && (
        <div style={{ background: "#FEF2F2", border: "2px solid #FECACA", borderRadius: 12, padding: "16px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>&#9888;</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#991B1B" }}>Billing Leakage — {leakEmps.length} employee{leakEmps.length > 1 ? "s" : ""} under-allocated</div>
            <div style={{ fontSize: 12, color: "#B91C1C", marginTop: 3 }}>{leakEmps.map(e => `${(e.name || "").split(" ")[0]} (${gapPct(e)}% gap)`).join(" · ")}</div>
          </div>
          <button className="btn-primary" style={{ background: "#EF4444", fontSize: 12, flexShrink: 0 }} onClick={() => setFilter("leakage")}>View All</button>
        </div>
      )}

      <div className="card" style={{ padding: "14px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", background: "#F8FAFC", borderRadius: 10, padding: 4, width: "fit-content", border: "1.5px solid #E2E8F0" }}>
          {[
            { k: "all", label: "All Employees", c: "#0D1B2A" },
            { k: "leakage", label: "Billing Leakage", c: "#EF4444" },
            { k: "unallocated", label: "Unallocated", c: "#F59E0B" },
            { k: "under", label: "Under-Allocated", c: "#3B82F6" },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} style={{
              padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: filter === f.k ? "#fff" : "transparent",
              color: filter === f.k ? f.c : "#64748B",
              fontFamily: "'DM Sans',sans-serif", fontSize: 12,
              fontWeight: filter === f.k ? 700 : 500,
              boxShadow: filter === f.k ? "0 1px 4px rgba(0,0,0,.1)" : "none",
              transition: "all .18s", display: "flex", alignItems: "center", gap: 6
            }}>
              {f.label}
              <span style={{ background: filter === f.k ? f.c + "18" : "#E2E8F0", color: filter === f.k ? f.c : "#94A3B8", borderRadius: 100, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{counts[f.k]}</span>
            </button>
          ))}
        </div>
        {/* Search box — placed before the sort dropdown, matches Review Management toolbar */}
        <div style={{ position: "relative", marginLeft: "auto", minWidth: 240 }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search employee, ID, role…"
            style={{
              width: "100%", padding: "8px 32px 8px 12px", fontSize: 12,
              borderRadius: 8, border: "1.5px solid #E2E8F0", background: "#fff",
              outline: "none", fontFamily: "'DM Sans',sans-serif", color: "#0D1B2A",
            }}
          />
          {search && (
            <button onClick={() => setSearch("")}
              title="Clear search"
              style={{
                position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", color: "#94A3B8", cursor: "pointer",
                fontSize: 16, lineHeight: 1, padding: "2px 6px",
              }}>&times;</button>
          )}
        </div>
        <select className="inp" value={dropdownVal} onChange={e => applyDropdownSort(e.target.value)}
          style={{ fontSize: 12, padding: "8px 10px", width: "auto", minWidth: 200 }}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtered.length === 0 && (
          <div className="card" style={{ padding: "32px", textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            {searchNorm
              ? <>No employees match <strong style={{ color: "#475569" }}>"{search}"</strong>.</>
              : "All clear — no employees match this filter."}
          </div>
        )}
        {filtered.map(emp => {
          const tot = totalPct(emp);
          const gap = gapPct(emp);
          const isEd = empSel === emp.id;
          const isLeak = gap > 0;
          const borderColor = tot === 0 ? "#FECACA" : tot < 100 ? "#FDE68A" : "#E2E8F0";
          return (
            <div key={emp.id} className="card" style={{ border: `1.5px solid ${borderColor}`, overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                <Avatar name={emp.name} size={38} />
                <div style={{ minWidth: 0, width: 170, flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0D1B2A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</div>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1 }}>{emp.code} · {emp.role}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", height: 10, borderRadius: 100, overflow: "hidden", background: "#F1F5F9", marginBottom: 6 }}>
                    {(emp.allocations || []).map((a, i) => <div key={i} title={`${a.clientName}: ${a.pct}%`} style={{ width: `${a.pct}%`, background: a.color }} />)}
                    {gap > 0 && <div style={{ width: `${gap}%`, background: "#FEE2E2" }} />}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {(emp.allocations || []).map((a, i) => (
                      <span key={i} style={{ fontSize: 10, color: a.color, background: a.color + "18", padding: "2px 7px", borderRadius: 100, fontWeight: 700 }}>{(a.clientName || "").split(" ")[0]} {a.pct}%</span>
                    ))}
                    {gap > 0 && <span style={{ fontSize: 10, color: "#EF4444", background: "#FEE2E2", padding: "2px 7px", borderRadius: 100, fontWeight: 700 }}>Unallocated {gap}%</span>}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, width: 72 }}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: -1, color: tot === 100 ? "#10B981" : tot === 0 ? "#EF4444" : "#F59E0B", lineHeight: 1 }}>{tot}%</div>
                  <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 2 }}>{tot === 100 ? "Fully allocated" : tot === 0 ? "Unallocated" : `${gap}% gap`}</div>
                </div>
                {currentRole !== "Sub-Admin" && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {!isEd && (
                      <button className={isLeak ? "btn-primary" : "btn-secondary"} style={{ fontSize: 12, ...(isLeak ? { background: "#EF4444" } : {}) }} onClick={() => openEdit(emp)}>
                        {isLeak ? "Fix Allocation" : "Edit"}
                      </button>
                    )}
                    {isEd && <button className="btn-ghost" style={{ fontSize: 12 }} onClick={closeEdit}>Cancel</button>}
                  </div>
                )}
              </div>

              {!isEd && (emp.allocations || []).length > 0 && (
                <div style={{ borderTop: "1px solid #F1F5F9", padding: "9px 20px", background: "#FAFBFF", display: "flex", flexWrap: "wrap", gap: 14 }}>
                  {(emp.allocations || []).map(a => {
                    const co = clients.find(c => c.id === a.clientId);
                    const hasCustom = a.stakeholders && a.stakeholders.length > 0;
                    if (hasCustom) {
                      return (
                        <div key={a.clientId} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: a.color }}>{(a.clientName || "").split(" ")[0]}</span>
                          <span style={{ fontSize: 11, color: "#CBD5E1" }}>&#8594;</span>
                          {a.stakeholders.map((s, si) => {
                            const sh = (co ? (co.stakeholders || []).find(x => x.id === s.stakeholderId) : null) || (s.name ? { name: s.name } : null);
                            return (
                              <span key={s.stakeholderId} style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 11, background: "#F5F3FF", border: "1px solid #DDD6FE",
                                borderRadius: 100, padding: "2px 8px"
                              }}>
                                <Avatar name={sh ? sh.name : "?"} size={14} />
                                <span style={{ fontWeight: 600, color: "#4C1D95" }}>{sh ? (sh.name || [sh.firstName, sh.lastName].filter(Boolean).join(" ") || "?").split(" ").slice(0, 2).join(" ") : "?"}</span>
                                <span style={{ fontWeight: 800, color: "#6D28D9" }}>{s.pct}%</span>
                                {si < a.stakeholders.length - 1 && <span style={{ color: "#C4B5FD" }}>·</span>}
                              </span>
                            );
                          })}
                        </div>
                      );
                    }
                    const primaryId = co ? (co.primaryStakeholderId ? co.primaryStakeholderId : ((co.stakeholders || []).filter(s => s.active && s.level === "client")[0] || {}).id) : null;
                    const primarySh = co ? (co.stakeholders || []).find(s => s.id === primaryId) : null;
                    return (
                      <div key={a.clientId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: a.color }}>{(a.clientName || "").split(" ")[0]}</span>
                        <span style={{ fontSize: 11, color: "#CBD5E1" }}>&#8594;</span>
                        {primarySh ? (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            fontSize: 11, background: a.color + "10", border: "1px solid " + a.color + "33",
                            borderRadius: 100, padding: "2px 8px"
                          }}>
                            <Avatar name={primarySh.name} size={14} />
                            <span style={{ fontWeight: 600, color: a.color }}>{(primarySh.name || [primarySh.firstName, primarySh.lastName].filter(Boolean).join(" ") || "?").split(" ").slice(0, 2).join(" ")}</span>
                            <span style={{ fontSize: 10, color: "#94A3B8", fontStyle: "italic" }}>primary</span>
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, color: "#94A3B8", fontStyle: "italic" }}>No stakeholder set</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {isEd && (
                <div className="fade-in" style={{ borderTop: "2px dashed #E2E8F0", background: "#FAFBFF", padding: "18px 20px" }}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: "#0D1B2A", marginBottom: 12 }}>
                    Edit Allocation — {emp.name}
                    <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, color: "#64748B" }}>Total must equal 100%</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                    {drafts.map((a, idx) => {
                      const co = clients.find(c => c.id === a.clientId);
                      const activeStakeholders = co ? (co.stakeholders || []).filter(s => s.active) : [];
                      const hasCustomSplit = (a.stakeholders || []).length > 0;
                      const shTotal = (a.stakeholders || []).reduce((s, x) => s + Number(x.pct), 0);
                      const shValid = !hasCustomSplit || shTotal === Number(a.pct);
                      return (
                        <div key={idx} style={{ background: "#fff", border: `1.5px solid ${a.color}44`, borderLeft: `4px solid ${a.color}`, borderRadius: 10, overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
                            <div style={{ width: 12, height: 12, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                            <select className="inp" value={a.clientId} onChange={e => updClient(idx, e.target.value)} style={{ width: 160, fontSize: 12, padding: "6px 9px" }}>
                              {clients.filter(c => c.status === "active").map(c => <option key={c.id} value={c.id} disabled={drafts.some((d, i) => i !== idx && d.clientId === c.id)}>{(c.name || "").split(" ")[0]}</option>)}
                            </select>
                            <select className="inp" value={a.deptId} onChange={e => updDept(idx, e.target.value)} style={{ width: 150, fontSize: 12, padding: "6px 9px" }}>
                              {(co ? co.departments : []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <input className="inp" type="number" min={1} max={100} value={a.pct} onChange={e => updPct(idx, e.target.value)} style={{ width: 68, fontSize: 13, fontWeight: 700, textAlign: "center", padding: "6px 9px", color: a.color, borderColor: a.color + "44" }} onKeyDown={(e) => { if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault(); }} />
                            <span style={{ fontSize: 13, color: "#64748B" }}>%</span>
                            {[25, 50, 75, 100].map(v => (
                              <button key={v} onClick={() => updPct(idx, v)} style={{ background: a.pct === v ? a.color + "22" : "#F1F5F9", color: a.pct === v ? a.color : "#64748B", border: `1.5px solid ${a.pct === v ? a.color : "#E2E8F0"}`, borderRadius: 6, padding: "4px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>{v}%</button>
                            ))}
                            <button className="btn-danger" onClick={() => remDraft(idx)} style={{ marginLeft: "auto" }}>Remove</button>
                          </div>

                          {activeStakeholders.length > 0 && (
                            <div style={{ borderTop: `1px dashed ${a.color}33`, background: "#F5F3FF", padding: "10px 14px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#6D28D9" }}>
                                  🎯 Stakeholder Split <span style={{ color: "#EF4444" }}>*</span>
                                </span>
                                <span style={{ fontSize: 10, color: shValid ? "#94A3B8" : "#DC2626" }}>
                                  Required — must total {a.pct}%
                                </span>
                                {hasCustomSplit && (
                                  <button onClick={() => resetStakeholderSplit(idx)} style={{ marginLeft: "auto", fontSize: 10, color: "#EF4444", background: "none", border: "1px solid #FECACA", borderRadius: 6, padding: "2px 8px", cursor: "pointer" }}>
                                    Reset to default
                                  </button>
                                )}
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                {activeStakeholders.map(sh => {
                                  const entry = (a.stakeholders || []).find(s => s.stakeholderId === sh.id);
                                  const shPct = entry ? entry.pct : "";
                                  return (
                                    <div key={sh.id} style={{
                                      display: "flex", alignItems: "center", gap: 6,
                                      background: "#fff", border: `1.5px solid ${entry ? "#8B5CF6" : "#E2E8F0"}`,
                                      borderRadius: 8, padding: "6px 10px", minWidth: 190
                                    }}>
                                      <Avatar name={sh.name} size={22} />
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sh.name}</div>
                                        <div style={{ fontSize: 10, color: "#94A3B8" }}>
                                          {sh.level === "client" ? "All employees" : "Dept only"}
                                        </div>
                                      </div>
                                      <input type="number" min={0} max={a.pct}
                                        value={shPct}
                                        placeholder="0"
                                        onChange={e => updStakeholderPct(idx, sh.id, e.target.value, Number(a.pct))}
                                        onKeyDown={(e) => { if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault(); }}
                                        style={{
                                          width: 46, fontSize: 12, fontWeight: 700, textAlign: "center",
                                          padding: "4px 6px", borderRadius: 6, border: `1.5px solid ${entry ? "#8B5CF6" : "#E2E8F0"}`,
                                          color: entry ? "#6D28D9" : "#94A3B8", outline: "none"
                                        }} />
                                      <span style={{ fontSize: 11, color: "#94A3B8" }}>%</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ flex: 1, height: 6, borderRadius: 100, background: "#E2E8F0", overflow: "hidden" }}>
                                  <div style={{
                                    width: `${Math.min((shTotal / Number(a.pct)) * 100, 100)}%`, height: "100%",
                                    background: shValid ? "#8B5CF6" : "#EF4444", transition: "width .2s"
                                  }} />
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: shValid ? "#6D28D9" : "#EF4444", flexShrink: 0 }}>
                                  {shTotal}/{a.pct}% {shValid ? "✓" : "— must equal " + a.pct + "%"}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <button className="btn-secondary" style={{ fontSize: 12 }} onClick={addDraft} disabled={drafts.length >= clients.filter(c => c.status === "active").length}>+ Add Client</button>
                    <div style={{ flex: 1, height: 8, borderRadius: 100, background: "#F1F5F9", overflow: "hidden" }}>
                      {drafts.map((a, i) => <div key={i} style={{ width: `${a.pct}%`, height: "100%", background: a.color, display: "inline-block" }} />)}
                    </div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, letterSpacing: -1, color: draftTotal === 100 ? "#10B981" : draftTotal > 100 ? "#EF4444" : "#F59E0B" }}>{draftTotal}%</div>
                    {draftGap > 0 && <span style={{ fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{draftGap}% remaining</span>}
                    {draftTotal > 100 && <span style={{ fontSize: 12, color: "#EF4444", fontWeight: 700 }}>Exceeds 100%</span>}
                  </div>
                  {/* #7 — block save when any drafted client has zero active stakeholders */}
                  {(() => {
                    const clientsWithoutSH = drafts
                      .map(a => clients.find(c => c.id === a.clientId))
                      .filter(c => c && !(c.stakeholders || []).some(s => s.active));
                    if (clientsWithoutSH.length > 0) {
                      return (
                        <div style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", borderRadius: 10, padding: "12px 16px", fontSize: 12, color: "#991B1B", lineHeight: 1.6 }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ No stakeholders configured</div>
                          {clientsWithoutSH.map(c => (
                            <div key={c.id}>• <strong>{c.name}</strong> has no active stakeholders — add at least one stakeholder in Client Configuration before allocating resources to this client.</div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn-primary" disabled={draftTotal > 100} onClick={() => saveDrafts(emp.id)} style={{ fontSize: 13 }}>Save Allocation</button>
                        <button className="btn-ghost" onClick={closeEdit} style={{ fontSize: 13 }}>Cancel</button>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {toast && <Toast msg={toast} type={toastType} />}
    </div>
  );
};

export default AllocationPage;